/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useRef, useState } from "react";
import {
  I2cAvatar,
  I2cBadge,
  I2cDatePicker,
  I2cFileUpload,
  I2cInput,
  I2cListItem,
  I2cMenuItem,
  I2cSelect,
  I2cSignature,
  I2cTextarea,
} from "@webcomponents/i2cwebcomponents/dist/react";
import FilesUploadService from "../../../Services/FilesUploadService";
import "../../PrepaidComponents/Prepaid.scss";

const stakeholders = [
  { key: "architect", label: "Solution Architect" },
  { key: "scrum_master", label: "Scrum Master" },
  { key: "project_manager", label: "Project Manager" },
  { key: "po", label: "PO Engineer" },
];

const I2cStakeHolderReviewSignoff = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
  stakeholderPermissions,
  formType = "prepaid", // "prepaid" (index 12) or "credit" (index 11)
}) => {
  const [formData, setFormData] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const signatureRefs = useRef({});

  // Determine form key based on formType (still needed for validation key)
  const formKey = formType === "credit" ? "12_0" : "12_0";

  // Get current stakeholder info from permissions
  const currentStakeholderType = stakeholderPermissions?.stakeholder_type;
  const canEditSignoff = stakeholderPermissions?.can_edit_signoff === true;

  const getDefaultStatus = (stakeholderKey) => {
    const defaultStatus =
      canEditSignoff && currentStakeholderType === stakeholderKey ? "approved" : "";
    return defaultStatus;
  };

  const shouldUseDefaultStatus = (stakeholderKey, status) =>
    canEditSignoff &&
    currentStakeholderType === stakeholderKey &&
    (!status || status === "pending");


  // Log formData for debugging
  useEffect(() => {
  }, [
    formData,
    formDataJson,
    stakeholderPermissions,
    currentStakeholderType,
    canEditSignoff,
    isFetching,
    isInitialized,
  ]);

  // Helper function to transform API array to object format
  const transformApiDataToFormData = (apiData) => {

    if (!Array.isArray(apiData)) {
      const transformedObjectData = Object.entries(apiData).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: {
            ...value,
            status: shouldUseDefaultStatus(key, value?.status)
              ? getDefaultStatus(key)
              : value?.status || "",
          },
        }),
        {}
      );
      return transformedObjectData;
    }

    const transformed = {};
    stakeholders.forEach(({ key }) => {
      transformed[key] = {
        name: "",
        title: "",
        status: getDefaultStatus(key),
        date: "",
        comments: "",
        signature: "",
      };
    });
 
    // Map API data to our structure
    apiData.forEach((item) => {
      const key = item.type; // "po", "architect", "project_manager", "scrum_master"
      if (transformed[key]) {
        transformed[key] = {
          name: item.user_name || "",
          title: item.job_title || "", 
          status: shouldUseDefaultStatus(key, item.status)
            ? getDefaultStatus(key)
            : item.status || "",
          date: item.signoff_at || "",
          comments: item.comments || "",
          signature: item.signature || "",
        };
      }
    });
 
    return transformed;
  };

  // Initialize form data only once when component mounts or when fetching
  useEffect(() => {
    if (!isFetching && !isInitialized) {
      if (formDataJson && formDataJson.stakeholder_signoff) { 
        const transformedData = transformApiDataToFormData(formDataJson.stakeholder_signoff);
        setFormData(transformedData);
        if (setAddFormData) {
          setAddFormData((prev) => ({
            ...prev,
            stakeholder_signoff: transformedData,
          }));
        }
      } else {
        console.log("Initializing with empty structure");
        // Initialize with empty structure
        const initialData = {};
        stakeholders.forEach(({ key }) => {
          initialData[key] = {
            name: "",
            title: "",
            status: getDefaultStatus(key),
            date: "",
            comments: "",
            signature: "",
          };
        });
        setFormData(initialData);
        if (setAddFormData) {
          setAddFormData((prev) => ({
            ...prev,
            stakeholder_signoff: initialData,
          }));
        }
      }
      setIsInitialized(true);
    }
  }, [isFetching, isInitialized]);

  // Update formData when formDataJson changes (for when data is loaded from API).
  // Important: do NOT call setAddFormData here. The parent feeds addFormData.stakeholder_signoff
  // back into formDataJson, so writing to it from this effect creates an update loop that
  // constantly reassigns props on the I2cSelect and clobbers the user's selection.
  // The user's edits are already pushed to the parent via handleValueChange.
  useEffect(() => {
    if (formDataJson?.stakeholder_signoff && isInitialized && isFetching) {
      const transformedData = transformApiDataToFormData(formDataJson.stakeholder_signoff);
      setFormData(transformedData);
    }
  }, [formDataJson?.stakeholder_signoff, isInitialized, isFetching]);

  // Update parent form data
  const handleValueChange = (stakeholderKey, field, value) => {
    const updatedFormData = {
      ...formData,
      [stakeholderKey]: {
        ...formData[stakeholderKey],
        [field]: value,
      },
    };

    setFormData(updatedFormData);

    if (setAddFormData) {
      setAddFormData((prev) => ({
        ...prev,
        stakeholder_signoff: updatedFormData,
      }));
    }

    // Check if current stakeholder has selected a status (minimum requirement)
    const currentStakeholderData = updatedFormData[stakeholderKey];
    const isValid = currentStakeholderData?.status ? true : false;

    if (setFormValidStates) {
      setFormValidStates(formKey, isValid);
    }
  };

  // NOTE: Removed the previous useEffect that auto-forced status to "approved"
  // whenever formData changed. It was redundant (initialization already applies the
  // default via getDefaultStatus) and it could revert the user's status selection
  // back to "approved" if any intermediate state had an empty/pending status.

  const handleSignatureUpload = async (stakeholderKey) => {
    
    try {
      const signatureComponent = signatureRefs.current[stakeholderKey];
      
      if (signatureComponent?.fileUpload?.files?.[0]) {
        const formData = new FormData();
        formData.append("files", signatureComponent.fileUpload.files[0]);
        const response = await FilesUploadService.uploadFileBase64(formData);
        
        if (response?.data?.url) {
          handleValueChange(stakeholderKey, "signature", response.data.url);
        }
      } else {
        console.log("No file found in signature component");
      }
    } catch (error) {
      console.error("Error uploading signature:", error);
    }
  };

  const handleSignatureDrawn = (stakeholderKey) => {
    
    try {
      const signatureComponent = signatureRefs.current[stakeholderKey];
      
      if (signatureComponent && typeof signatureComponent.getDataURL === 'function') {
        const signatureData = signatureComponent.getDataURL("JPG");
        handleValueChange(stakeholderKey, "signature", signatureData);
      } else {
        console.log("Signature component does not have getDataURL method");
      }
    } catch (error) {
      console.error("Error getting drawn signature:", error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <I2cBadge variant="light-success">
            Signed Off
          </I2cBadge>
        );
      case "feedback":
        return (
          <I2cBadge variant="light-warning">
            Feedback
          </I2cBadge>
        );
      case "rejected":
        return (
          <I2cBadge variant="light-danger">
            Rejected
          </I2cBadge>
        );
      default:
        return (
          <I2cBadge variant="light-gray">
            Pending
          </I2cBadge>
        );
    }
  };

  return (
    <React.Fragment>
      <div className="forms-instance" i2c-grid="grid gap-sm">
        <div i2c-grid="12">
          {stakeholders.map(({ key, label }) => {
            // Determine if this is the current stakeholder's editable section
            const isEditableSection = currentStakeholderType === key && canEditSignoff;
            
            // Get initials from label
            const initials = label.split(' ').map(word => word[0]).join('').toUpperCase();
            
            // Check if stakeholder has submitted data
            const hasData = formData[key]?.status;

            // Only show:
            // 1. Current stakeholder's editable section OR
            // 2. Other stakeholders who have already submitted (hasData)
            if (!isEditableSection && !hasData) {
              return null; // Don't show stakeholders who haven't submitted yet
            }

            // If this is the current stakeholder's section, show full editable form
            if (isEditableSection) {
              return (
                <I2cListItem
                  key={key}
                  label={label}
                  variant="default"
                  className="signoff-active mb-16"
                >
                  <div slot="value" className="stakeholder-signoff-value">
                    <div i2c-grid="grid gap-md">
                      <div i2c-grid="6@md">
                        <div i2c-grid="grid gap-sm">
                          <div i2c-grid="6@md">
                            <div className="single-field-wrapper">
                              <I2cInput
                                label="Name"
                                readonly
                                value={formData[key]?.name || ""}
                                placeholder="Enter Name"
                                size="large"
                                name={`${key}_name`}
                                onI2cInput={(e) =>
                                  handleValueChange(key, "name", e.target.value)
                                }
                              />
                            </div>
                          </div>
                          <div i2c-grid="6@md">
                            <div className="single-field-wrapper">
                              <I2cInput
                                placeholder="Enter Title"
                                size="large"
                                readonly
                                label="Title"
                                name={`${key}_title`}
                                value={formData[key]?.title || ""}
                                onI2cInput={(e) =>
                                  handleValueChange(key, "title", e.target.value)
                                }
                              />
                            </div>
                          </div>
                          <div i2c-grid="6@md">
                            <div className="single-field-wrapper">
                              <I2cSelect
                                size="large"
                                label="Status"
                                name={`${key}_status`}
                                placeholder="Select Status"
                                value={formData[key]?.status || ""}
                                onI2cChange={(e) =>
                                  handleValueChange(key, "status", e.target.value)
                                }
                              >
                                <I2cMenuItem value="approved">Approved</I2cMenuItem>
                                <I2cMenuItem value="feedback">Feedback</I2cMenuItem>
                                <I2cMenuItem value="rejected">Rejected</I2cMenuItem>
                              </I2cSelect>
                            </div>
                          </div>
                          <div i2c-grid="6@md">
                            <div className="single-field-wrapper">
                              <I2cDatePicker
                                placeholder="Select Date"
                                hoist
                                suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                                errorMessageInvisible={true}
                                size="large"
                                label="Date"
                                rotateIcon
                                name={`${key}_date`}
                                dateFormat="M d, Y"
                                value={formData[key]?.date || ""}
                                onI2cDateApply={(e) => {
                                  if (e.target?.appliedDate) {
                                    handleValueChange(
                                      key,
                                      "date",
                                      e.target?.appliedDate[0]?.toDateString() ?? ""
                                    );
                                  } else {
                                    handleValueChange(key, "date", "");
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div i2c-grid="12">
                            <div className="single-field-wrapper">
                              <I2cTextarea
                                placeholder="No Additional Comments"
                                size="large"
                                label="Comments"
                                name={`${key}_comments`}
                                rows={4}
                                value={formData[key]?.comments || ""}
                                onI2cInput={(e) =>
                                  handleValueChange(key, "comments", e.target.value)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div i2c-grid="6@md">
                        <I2cSignature
                          className="stakeholder-signature"
                          ref={(el) => (signatureRefs.current[key] = el)}
                          show-alert={true}
                          value={formData[key]?.signature || ""}
                          onI2cSignatureEndStroke={() => {
                            console.log("Signature drawn for:", key);
                            handleSignatureDrawn(key);
                          }}
                        >
                          <I2cFileUpload
                            slot="upload"
                            color="primary"
                            no-mobile-controls
                            icon="linear-document-upload"
                            label="Click to upload"
                            caption="or drag and drop"
                            message="SVG, PNG, JPG or GIF (max. 800x400px)."
                            help-text="This is some supporting text to accompany progress bar."
                            max-files={1}
                            setAcceptedFiles=".png,.jpg,.jpeg"
                            autoProcessQueue={false}
                            onI2cAddedFile={(e) => { 
                              handleSignatureUpload(key);
                            }}
                            onI2cFileRemoved={(e) => { 
                              handleValueChange(key, "signature", "");
                            }}
                          ></I2cFileUpload>
                        </I2cSignature>
                      </div>
                    </div>
                  </div>
                </I2cListItem>
              );
            }

            // For all other stakeholders, show compact readonly card (always visible)
            return (
              <I2cListItem
                key={key}
                variant="default"
                label={label}
                part="label"
                className="I2cStakeHolderReviewSignoff mb-16"
              >
                <I2cAvatar
                  slot="prefix"
                  initials={initials}
                  size="large"
                  image=""
                  label={formData[key]?.name || label}
                ></I2cAvatar>

                <div slot="suffix" className="i2cStakeHolderReviewStatus">
                  {hasData ? (
                    <>
                      {getStatusBadge(formData[key]?.status)}
                      {formData[key]?.date && (
                        <div className="i2cStakeHolderDate">{formData[key]?.date}</div>
                      )}
                    </>
                  ) : (
                    <I2cBadge variant="light-gray">Pending</I2cBadge>
                  )}
                </div>

                <div slot="value" className="stakeHolderInfoWrapper">
                  {hasData ? (
                    <>
                      <div className="stakeHolderInfo">
                        <div className="i2cStakeHolderInfoHeading">Name:</div>
                        <div className="i2cStakeHolderInfo">
                          {formData[key]?.name || "Not Provided"}
                        </div>
                      </div>
                      •
                      <div className="stakeHolderInfo">
                        <div className="i2cStakeHolderInfoHeading">Job Title:</div>
                        <div className="i2cStakeHolderInfo">
                          {formData[key]?.title || "Not Provided"}
                        </div>
                      </div>
                      •
                      <div className="stakeHolderInfo">
                        <div className="i2cStakeHolderInfoHeading">Comment:</div>
                        <div className="i2cStakeHolderInfo">
                          {formData[key]?.comments || "No comments"}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="stakeHolderInfo">
                      <div className="i2cStakeHolderInfo">Awaiting Reviewer</div>
                    </div>
                  )}
                </div>
              </I2cListItem>
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
};

export default I2cStakeHolderReviewSignoff;
